import FullWidthStaticLayout from '@/Layout/FullWidthStaticLayout';

const PrivacyPage = () => {
  return (
    <FullWidthStaticLayout>
      <h1>Privacy Policy</h1>
      <h2>ROKiT iD</h2>
      <h3>Operated by ROKiT Group</h3>

      <h2>1. Introduction</h2>

      <p>
        This Privacy Policy explains how <strong>ROKiT Group</strong> ("ROKiT", "we", "our", or "us") collects, uses,
        stores, shares, and protects personal information relating to users of <strong>ROKiT iD</strong>.
      </p>

      <p>
        ROKiT iD is an account registration, authentication, and single sign-on service that allows users to create
        and manage one account for access to ROKiT services, websites, apps, membership platforms, media services,
        communications services, and related products, including services that may operate under the <strong>Enigma</strong> or <strong>Riddle</strong> names.
      </p>

      <p>
        By using ROKiT iD, you acknowledge the practices described in this Privacy Policy.
      </p>

      <h2>2. Information We Collect</h2>

      <p>We may collect the following categories of information:</p>

      <h3>a. Account Registration Information</h3>
      <ul>
        <li>Name</li>
        <li>Username</li>
        <li>Email address</li>
        <li>Telephone number
          <br/><i>
            By registering for a ROKiT iD account and providing your mobile number, you consent to receive SMS messages for authentication, account verification, and security purposes (such as one-time passwords).
          </i>

        </li>
        <li>Password or authentication credentials</li>
        <li>Date of birth or age confirmation, where required</li>
        <li>Profile photo or avatar, where provided</li>
      </ul>

      <h3>b. Authentication and Security Information</h3>
      <ul>
        <li>Login history</li>
        <li>Session identifiers</li>
        <li>Device identifiers</li>
        <li>IP addresses</li>
        <li>Browser and app information</li>
        <li>Security verification logs</li>
        <li>Multi-factor authentication settings</li>
        <li>Password reset and account recovery activity</li>
      </ul>

      <h3>c. Connected Service Information</h3>
      <ul>
        <li>Services you access using ROKiT iD</li>
        <li>Account linking information</li>
        <li>Service entitlements, memberships, subscriptions, or access rights</li>
        <li>Basic profile information shared with connected ROKiT services</li>
        <li>Support or account status information</li>
      </ul>

      <h3>d. Usage and Technical Information</h3>
      <ul>
        <li>App and website usage data</li>
        <li>Diagnostic logs</li>
        <li>Crash reports</li>
        <li>Performance information</li>
        <li>Approximate location derived from IP address</li>
        <li>Security and fraud prevention signals</li>
      </ul>

      <h3>e. Payment and Subscription Information</h3>
      <p>
        Where ROKiT iD is used to access paid services, memberships, or subscriptions, payment processing may be handled
        by third-party payment providers. ROKiT Group may receive limited billing, subscription, entitlement,
        or transaction information.
      </p>

      <h2>3. How We Use Information</h2>

      <p>We may use personal information to:</p>

      <ul>
        <li>create and manage your ROKiT iD account;</li>
        <li>authenticate you and enable single sign-on;</li>
        <li>allow access to connected ROKiT services;</li>
        <li>verify account ownership;</li>
        <li>provide account recovery and security features;</li>
        <li>manage subscriptions, memberships, and access rights;</li>
        <li>protect against fraud, abuse, credential attacks, and unauthorised access;</li>
        <li>maintain platform stability and security;</li>
        <li>provide customer support;</li>
        <li>send important account, security, and service notices;</li>
        <li>improve ROKiT iD and connected services;</li>
        <li>comply with legal obligations and enforce our terms.</li>
      </ul>

      <h2>4. Sharing with Connected ROKiT Services</h2>

      <p>
        When you use ROKiT iD to sign in to a ROKiT service, we may share limited account information with that service
        so that it can recognise you, create or link your service profile, verify access rights, provide features,
        manage subscriptions, deliver support, and maintain security.
      </p>

      <p>
        This may include your user ID, name, username, email address, profile image, account status, verification status,
        subscription or membership entitlement, and security-related information.
      </p>

      <h2>5. Sharing with Third Parties</h2>

      <p>
        We do not sell personal information to third parties.
      </p>

      <p>We may share information:</p>

      <ul>
        <li>with trusted infrastructure, hosting, identity, security, analytics, support, and notification providers;</li>
        <li>with payment processors and app store operators where required for paid services;</li>
        <li>where required by law, regulation, court order, or legal process;</li>
        <li>to protect users, ROKiT, connected services, rights, property, or safety;</li>
        <li>during mergers, acquisitions, financing, restructuring, or sale of business assets.</li>
      </ul>

      <h2>6. Cookies and Similar Technologies</h2>

      <p>
        ROKiT iD may use cookies, local storage, pixels, tokens, device identifiers, and similar technologies to support:
      </p>

      <ul>
        <li>login sessions;</li>
        <li>single sign-on functionality;</li>
        <li>security and fraud prevention;</li>
        <li>remembered devices;</li>
        <li>preferences;</li>
        <li>analytics and diagnostics;</li>
        <li>service performance.</li>
      </ul>

      <h2>7. Security</h2>

      <p>
        We use technical and organisational measures designed to protect personal information, including measures such as
        encryption, access controls, secure authentication, logging, monitoring, and security review.
      </p>

      <p>
        However, no electronic system, internet transmission, or storage method can be guaranteed to be completely secure.
        Users are responsible for protecting their passwords, devices, verification codes, and recovery methods.
      </p>

      <h2>8. Data Retention</h2>

      <p>We retain information only for as long as reasonably necessary to:</p>

      <ul>
        <li>provide ROKiT iD;</li>
        <li>maintain account access and security;</li>
        <li>support connected services;</li>
        <li>comply with legal obligations;</li>
        <li>resolve disputes;</li>
        <li>prevent fraud and abuse;</li>
        <li>enforce agreements.</li>
      </ul>

      <p>
        Certain information may remain in backups, security logs, audit records, or archived systems for limited periods.
      </p>

      <h2>9. International Transfers</h2>

      <p>
        Information may be processed and stored in countries outside your own jurisdiction.
        Where required, we will use appropriate safeguards for international transfers of personal information.
      </p>

      <h2>10. User Rights</h2>

      <p>
        Depending on your jurisdiction, you may have rights relating to:
      </p>

      <ul>
        <li>access to personal information;</li>
        <li>correction of inaccurate information;</li>
        <li>deletion of personal information;</li>
        <li>restriction of processing;</li>
        <li>objection to processing;</li>
        <li>data portability;</li>
        <li>withdrawal of consent, where processing is based on consent.</li>
      </ul>

      <p>
        Requests may be submitted using the contact details below. We may need to verify your identity before responding.
      </p>

      <h2>11. Account Deletion</h2>

      <p>
        You may request deletion of your ROKiT iD account, subject to any legal, security, fraud prevention,
        billing, dispute resolution, or operational retention requirements.
      </p>

      <p>
        Deleting your ROKiT iD account may affect your ability to access connected ROKiT services, memberships,
        subscriptions, purchases, content, or account history.
      </p>

      <h2>12. Children's Privacy</h2>

      <p>
        ROKiT iD is not intended for children under the applicable minimum digital consent age.
        We do not knowingly collect personal information from children in violation of applicable laws.
      </p>

      <h2>13. Marketing Communications</h2>

      <p>
        We may send service-related notices that are necessary for account operation, security, legal compliance,
        or access to connected services.
      </p>

      <p>
        Where permitted, we may also send marketing communications about ROKiT products or services.
        You may opt out of marketing communications where required by law.
      </p>

      <h2>14. Third-Party Services</h2>

      <p>
        ROKiT iD may integrate with third-party providers including:
      </p>

      <ul>
        <li>cloud hosting providers;</li>
        <li>identity and authentication providers;</li>
        <li>security and fraud prevention providers;</li>
        <li>payment processors;</li>
        <li>analytics services;</li>
        <li>email, SMS, and push notification services;</li>
        <li>mobile application marketplaces;</li>
        <li>customer support platforms.</li>
      </ul>

      <p>
        ROKiT Group is not responsible for the privacy practices of third-party services.
      </p>

      <h2>15. Changes to this Privacy Policy</h2>

      <p>
        We may update this Privacy Policy periodically.
        Where material changes are made, reasonable efforts may be used to notify users through ROKiT iD,
        connected services, email, or other appropriate methods.
      </p>

      <p>
        Continued use of ROKiT iD following updates constitutes acknowledgement of the revised policy.
      </p>

      <h2>16. Contact Information</h2>

      <p>
        For privacy-related enquiries, requests, or legal notices, please contact:
      </p>

      <p>
        <strong>ROKiT Group</strong>
        <br />
        Email: support@rokit.com
      </p>

      <hr />

      <small style={{ display: 'block', textAlign: 'center', marginTop: '1.5rem', color: 'var(--color-text-tertiary)' }}>
        Effective Date: 12 April 2026 | Last Updated: 12 April 2026
      </small>
    </FullWidthStaticLayout>
  );
};

export default PrivacyPage;
